import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Copy, CopyPlus, Lock, EyeOff, Trash2, Pipette, ClipboardPaste, Layers,
  AlignCenter, ChevronRight, ChevronsUp, ChevronUp, ChevronDown, ChevronsDown,
  AlignStartVertical, AlignCenterVertical, AlignEndVertical,
  AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal,
  Box, MessageSquare, Link as LinkIcon, TextCursorInput,
} from "lucide-react";

const W_MAIN = 248;
const W_SUB  = 224;

export default function ContextMenu({ x, y, multi, smartLayersOpen, onClose, onAction }) {
  const mainRef = useRef(null);
  const subRef  = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [sub, setSub] = useState(null); // {kind:'layer'|'align', left, top, openLeft}

  useLayoutEffect(() => {
    const w = W_MAIN;
    const h = mainRef.current?.offsetHeight || 440;
    let left = x, top = y;
    if (left + w + 12 > window.innerWidth)  left = Math.max(12, window.innerWidth - w - 12);
    if (top + h + 12 > window.innerHeight)  top  = Math.max(12, window.innerHeight - h - 12);
    setPos({ left, top });
  }, [x, y]);

  useEffect(() => {
    const onDoc = (e) => {
      if (mainRef.current?.contains(e.target)) return;
      if (subRef.current?.contains(e.target))  return;
      onClose();
    };
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const openSub = (kind, anchorRowEl) => {
    const r = anchorRowEl.getBoundingClientRect();
    const subW = W_SUB;
    const subH = kind === "layer" ? (smartLayersOpen ? 168 : 210) : 240;
    let openLeft = false;
    let left = r.right + 4;
    if (left + subW + 12 > window.innerWidth) { openLeft = true; left = Math.max(12, r.left - subW - 4); }
    let top = r.top;
    if (top + subH + 12 > window.innerHeight) top = Math.max(12, window.innerHeight - subH - 12);
    setSub({ kind, left, top, openLeft });
  };

  return (
    <>
      <div
        ref={mainRef}
        data-testid="md-ctx"
        style={{
          position: "fixed", left: pos.left, top: pos.top, width: W_MAIN, zIndex: 3800,
          background: "var(--pl-surface)", border: "1px solid var(--pl-border)",
          boxShadow: "0 24px 70px rgba(0,0,0,0.42)", borderRadius: 14, padding: 6,
          color: "var(--pl-text)", display: "flex", flexDirection: "column",
        }}
        className="backdrop-blur"
        onContextMenu={(e) => e.preventDefault()}
      >
        {multi && (
          <>
            <Row label="Group selected" shortcut="⌘G" data-testid="md-ctx-group" onClick={() => onAction("group")} icon={CopyPlus} />
            <Row label="Ungroup"        shortcut="⌘⇧G" onClick={() => onAction("ungroup")} icon={CopyPlus} />
            <Sep />
          </>
        )}
        <Row label="Copy"      shortcut="⌘C" data-testid="md-ctx-copy"      onClick={() => onAction("copy")} icon={Copy} />
        <Row label="Duplicate" shortcut="⌘D" data-testid="md-ctx-duplicate" onClick={() => onAction("duplicate")} icon={CopyPlus} />
        <Row label="Lock"      shortcut="⌘⇧L" data-testid="md-ctx-lock"     onClick={() => onAction("lock")} icon={Lock} />
        <Row label="Hide"                       data-testid="md-ctx-hide"   onClick={() => onAction("hide")} icon={EyeOff} />
        <Row label="Delete"    shortcut="⌫"   data-testid="md-ctx-delete"   onClick={() => onAction("delete")} icon={Trash2} />
        <SectionLabel>Clipboard</SectionLabel>
        <Row label="Copy"       onClick={() => onAction("copy")} icon={Copy} />
        <Row label="Copy style" onClick={() => onAction("copy-style")} icon={Pipette} />
        <Row label="Paste"      onClick={() => onAction("paste")} icon={ClipboardPaste} />
        <SectionLabel>Arrange</SectionLabel>
        <Row label="Layer"        data-testid="md-ctx-layer"
          submenu onClick={(e) => openSub("layer", e.currentTarget)} icon={Layers} />
        <Row label="Align to page" data-testid="md-ctx-align"
          submenu onClick={(e) => openSub("align", e.currentTarget)} icon={AlignCenter} />
        <SectionLabel>Actions</SectionLabel>
        <Row label="Create component" onClick={() => onAction("component")} icon={Box} />
        <Row label="Comment"          onClick={() => onAction("comment")}   icon={MessageSquare} />
        <Row label="Lock"             onClick={() => onAction("lock")}      icon={Lock} />
        <Row label="Link"             onClick={() => onAction("link")}      icon={LinkIcon} />
        <Row label="Alternative text" onClick={() => onAction("alt")}       icon={TextCursorInput} />
      </div>

      {sub && (
        <div
          ref={subRef}
          data-testid={`md-ctx-sub-${sub.kind}`}
          style={{
            position: "fixed", left: sub.left, top: sub.top, width: W_SUB, zIndex: 3900,
            background: "var(--pl-surface)", border: "1px solid var(--pl-border)",
            boxShadow: "0 24px 70px rgba(0,0,0,0.42)", borderRadius: 12, padding: 6,
            color: "var(--pl-text)",
          }}
          className="backdrop-blur"
        >
          {sub.kind === "layer" && (
            <>
              <Row label="Bring to front"  onClick={() => onAction("layer-front")} icon={ChevronsUp} />
              <Row label="Bring forward"   onClick={() => onAction("layer-up")}    icon={ChevronUp} />
              <Row label="Send backward"   onClick={() => onAction("layer-down")}  icon={ChevronDown} />
              <Row label="Send to back"    onClick={() => onAction("layer-back")}  icon={ChevronsDown} />
              {!smartLayersOpen && (
                <>
                  <Sep />
                  <Row label="Show layers" data-testid="md-ctx-show-layers" onClick={() => onAction("layers")} icon={Layers} />
                </>
              )}
            </>
          )}
          {sub.kind === "align" && (
            <>
              <Row label="Left"   onClick={() => onAction("align-left")}   icon={AlignStartVertical} />
              <Row label="Center" onClick={() => onAction("align-center")} icon={AlignCenterVertical} />
              <Row label="Right"  onClick={() => onAction("align-right")}  icon={AlignEndVertical} />
              <Sep />
              <Row label="Top"    onClick={() => onAction("align-top")}    icon={AlignStartHorizontal} />
              <Row label="Middle" onClick={() => onAction("align-middle")} icon={AlignCenterHorizontal} />
              <Row label="Bottom" onClick={() => onAction("align-bottom")} icon={AlignEndHorizontal} />
            </>
          )}
        </div>
      )}
    </>
  );
}

function Row({ label, shortcut, icon: I, submenu, onClick, "data-testid": testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full text-left rounded-lg px-2.5 py-2 text-[13px] font-medium grid items-center gap-2.5 hover:text-[var(--pl-aqua)] hover:bg-[var(--pl-aqua)]/10"
      style={{ gridTemplateColumns: "16px minmax(0,1fr) auto", color: "inherit", background: "transparent", border: 0, cursor: "pointer" }}
    >
      {I ? <I className="h-3.5 w-3.5" /> : <span />}
      <span>{label}</span>
      {submenu
        ? <ChevronRight className="h-3 w-3 opacity-50" />
        : <span className="text-[10px] tracking-[0.06em] opacity-50">{shortcut || ""}</span>}
    </button>
  );
}

function Sep() {
  return <div style={{ height: 1, background: "var(--pl-border)", margin: "4px 4px" }} />;
}
function SectionLabel({ children }) {
  return (
    <div className="px-2.5 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--pl-text-muted)]">
      {children}
    </div>
  );
}
